// ============================================================================
// Toast — QM Toast 2027 component
// ----------------------------------------------------------------------------
// v5.14.86. Replaces the v5.14.85 left-stripe-+-bottom-bar aesthetic with
// a "color as ambient light" approach (Peter rejected the stripe as the
// AI-default vibe-coder pattern). See `src/styles/qm-toast.css` header for
// the full design rationale.
//
// Visual anatomy (v2027):
//   ┌──────────────────────────────────────────────────────────┐
//   │   ◯◯◯                                                  × │  ◯ = soft aurora
//   │  ◯ [icon]  Title (optional)                              │  bloom behind icon.
//   │   ◯◯◯      Message body                                  │  Icon glyph sits
//   │            [ACTION]  [SECONDARY]                         │  in the centre of
//   └──────────────────────────────────────────────────────────┘  a draining ring.
//   • NO left accent stripe. NO top accent stripe. NO coloured borders.
//   • The type colour blooms as ambient light behind the icon — it tints
//     the surrounding glass and fades to transparent across the card.
//   • Countdown timer = SVG circular ring drawn around the icon. Drains
//     stroke-dashoffset 0 → circumference as time runs out.
//   • Inner-top hairline highlight on the surface = the liquid-glass
//     refraction edge. The faintest tonal wash of the type colour gives
//     each card its own subtle mood.
//
// Behaviour: spring entrance from the right (or below, for bottom-center
// stacks), hover-to-pause (pauses both the dismiss timer and the ring
// animation), reduced-motion respected.
// ============================================================================

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Icons } from './Icon';

// Toast type discriminator — keys the TYPE_ICON / TYPE_LABEL lookups.
type ToastType = 'info' | 'success' | 'warning' | 'error' | 'neutral';

// `Record<ToastType, …>` so string-keyed `TYPE_ICON[type]` / `TYPE_LABEL[type]`
// reads typecheck (a literal-keyed record, not an open index, so
// noUncheckedIndexedAccess doesn't widen to `| undefined`). Erases to the same
// object literals — no runtime change. The icon value is rendered as a JSX
// component (`<IconComp .../>`), so it must stay JSX-callable → `any` (the same
// Icon `Record<string, any>` ruling from batch 1; `Icons.*` are already `any`).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IconLike = any;
const TYPE_ICON: Record<ToastType, IconLike> = {
    info:    Icons.Info,
    success: Icons.CheckCircle || Icons.Check,
    warning: Icons.AlertTriangle,
    error:   Icons.AlertTriangle,
    neutral: Icons.Info,
};

// QuoteMaker construction-flavour TYPE LABEL — rendered above the title.
// Reads like a job-site clipboard tag or steel stamp. v5.14.92 addition.
const TYPE_LABEL: Record<ToastType, string> = {
    info:    'NOTICE',
    success: 'COMPLETE',
    warning: 'CAUTION',
    error:   'BLOCKED',
    neutral: 'STATUS',
};

const DEFAULT_DURATION = 4000;

// Geometry of the countdown ring drawn around the icon.
// v5.14.91: shell compacted from 40×40 to 28×28 (Peter: "the new toasts
// shouldn't be oversized. the old legacy toasts used to be a good size").
// SVG viewBox stays "0 0 28 28"; ring radius is 11 to leave 1.5px breathing
// room from the shell edge for the stroke + glow.
const RING_RADIUS = 11;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;   // ~69.1

// A toast action button: a label + click handler. `onClick` is invoked then the
// toast self-dismisses. Both fields optional (the component falls back to
// 'Action'/'Cancel' labels and a no-op via `?.`).
interface ToastAction {
    label?: string;
    onClick?: () => void;
}

interface ToastProps {
    type?: ToastType;
    title?: React.ReactNode;
    message?: React.ReactNode;
    action?: ToastAction;
    secondaryAction?: ToastAction;
    duration?: number;
    onDismiss?: () => void;
    /** Render-override for the icon glyph (when set, the type icon is skipped). */
    icon?: React.ReactNode;
    variant?: 'toast' | 'banner';
    /** Override the uppercase type tag; pass '' to suppress it entirely. */
    typeLabel?: string;
}

/**
 * The canonical toast card.
 * Props:
 *   type, title, message, action, secondaryAction, duration, onDismiss,
 *   icon (override), variant ('toast' | 'banner'),
 *   typeLabel (override the construction-flavour uppercase tag — pass
 *              empty string '' to suppress it entirely).
 */
export function Toast({
    type = 'info',
    title,
    message,
    action,
    secondaryAction,
    duration = DEFAULT_DURATION,
    onDismiss,
    icon,
    variant = 'toast',
    typeLabel,
}: ToastProps) {
    const [exiting, setExiting] = useState(false);
    const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const remainingRef = useRef(duration);
    const startedAtRef = useRef(Date.now());

    const handleDismiss = useCallback(() => {
        if (exiting) return;
        setExiting(true);
        if (dismissTimerRef.current) {
            clearTimeout(dismissTimerRef.current);
            dismissTimerRef.current = null;
        }
        // 220ms matches the qmToastExit keyframe in qm-toast.css.
        setTimeout(() => { onDismiss?.(); }, 220);
    }, [exiting, onDismiss]);

    useEffect(() => {
        if (variant === 'banner') return;
        if (!duration || duration <= 0) return;
        startedAtRef.current = Date.now();
        remainingRef.current = duration;
        dismissTimerRef.current = setTimeout(handleDismiss, duration);
        return () => {
            if (dismissTimerRef.current) {
                clearTimeout(dismissTimerRef.current);
                dismissTimerRef.current = null;
            }
        };
    }, [duration, variant, handleDismiss]);

    const onMouseEnter = useCallback(() => {
        if (!dismissTimerRef.current) return;
        const elapsed = Date.now() - startedAtRef.current;
        remainingRef.current = Math.max(0, duration - elapsed);
        clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
    }, [duration]);

    const onMouseLeave = useCallback(() => {
        if (variant === 'banner') return;
        if (!duration || duration <= 0) return;
        if (dismissTimerRef.current) return;
        startedAtRef.current = Date.now() - (duration - remainingRef.current);
        dismissTimerRef.current = setTimeout(handleDismiss, remainingRef.current);
    }, [duration, variant, handleDismiss]);

    const IconComp = icon ? null : (TYPE_ICON[type] || TYPE_ICON.info);
    const showRing = variant !== 'banner' && duration > 0;
    // typeLabel === '' explicitly suppresses; undefined falls back to default
    const resolvedLabel = typeLabel === undefined
        ? (TYPE_LABEL[type] || TYPE_LABEL.info)
        : typeLabel;

    return (
        <div
            className={`qm-toast qm-toast--${type}${variant === 'banner' ? ' qm-toast--banner' : ''}${exiting ? ' qm-toast--exiting' : ''}`}
            // CSS custom properties aren't part of React.CSSProperties; cast is
            // compile-erased (the object literal is emitted byte-for-byte).
            style={{
                ['--qm-toast-duration']: `${duration}ms`,
                ['--qm-toast-ring-circumference']: RING_CIRCUMFERENCE.toFixed(3),
            } as React.CSSProperties}
            role={type === 'error' || type === 'warning' ? 'alert' : 'status'}
            aria-live={type === 'error' || type === 'warning' ? 'assertive' : 'polite'}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <span className="qm-toast__icon" aria-hidden="true">
                {/* The countdown ring is drawn underneath the icon glyph.
                    For banners (no auto-dismiss) the track stays as a subtle
                    ambient ring around the icon — looks intentional. */}
                <svg
                    className="qm-toast__ring"
                    viewBox="0 0 28 28"
                    aria-hidden="true"
                >
                    <circle
                        className="qm-toast__ring-track"
                        cx="14" cy="14" r={RING_RADIUS}
                    />
                    {showRing && (
                        <circle
                            className="qm-toast__ring-progress"
                            cx="14" cy="14" r={RING_RADIUS}
                            strokeDasharray={RING_CIRCUMFERENCE}
                            strokeDashoffset="0"
                        />
                    )}
                </svg>
                <span className="qm-toast__icon-glyph">
                    {icon ?? <IconComp size={15} strokeWidth={2.2} />}
                </span>
            </span>
            <div className="qm-toast__body">
                {/* v5.14.94 — TYPE LABEL is now rendered INLINE with the
                    first text element (title if present, else message) so it
                    doesn't occupy its own row. Saves ~10px of vertical per
                    toast. */}
                {title && (
                    <p className="qm-toast__title">
                        {resolvedLabel && (
                            <span className="qm-toast__type-label">{resolvedLabel}</span>
                        )}
                        {title}
                    </p>
                )}
                {message && (
                    <p className="qm-toast__message">
                        {!title && resolvedLabel && (
                            <span className="qm-toast__type-label">{resolvedLabel}</span>
                        )}
                        {message}
                    </p>
                )}
            </div>
            <button
                type="button"
                className="qm-toast__close"
                onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
                aria-label="Dismiss notification"
            >
                <Icons.X size={16} aria-hidden="true" />
            </button>
            {(action || secondaryAction) && (
                <div className="qm-toast__actions">
                    {action && (
                        <button
                            type="button"
                            className="qm-toast__action"
                            onClick={(e) => {
                                e.stopPropagation();
                                try { action.onClick?.(); } finally { handleDismiss(); }
                            }}
                        >
                            {action.label || 'Action'}
                        </button>
                    )}
                    {secondaryAction && (
                        <button
                            type="button"
                            className="qm-toast__action"
                            style={{ opacity: 0.78 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                try { secondaryAction.onClick?.(); } finally { handleDismiss(); }
                            }}
                        >
                            {secondaryAction.label || 'Cancel'}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

interface ToastStackProps {
    position?: 'bottom-right' | 'bottom-center';
    children?: React.ReactNode;
}

/**
 * ToastStack — positioning container for stacked toasts.
 */
export function ToastStack({ position = 'bottom-right', children }: ToastStackProps) {
    const cls = position === 'bottom-center'
        ? 'qm-toast-stack qm-toast-stack--bottom-center'
        : 'qm-toast-stack';
    return (
        <div className={cls}>
            {children}
        </div>
    );
}

export default Toast;
